import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import Reveal from "../components/Reveal";
import { MAILTO } from "../lib/brand";

const steps = [
    {
        n: "01",
        title: "Brand defines goal",
        body: "FreeByte builds a custom-made campaign designed to grow the brand's specific objective.",
    },
    {
        n: "02",
        title: "Earn",
        body: "Users earn FreeByte by participating in the brand's campaign.",
    },
    {
        n: "03",
        title: "Save",
        body: "Users collect and keep their FreeByte balance over time, ready to use.",
    },
    {
        n: "04",
        title: "Redeem",
        body: "Users redeem FreeByte for mobile data or airtime when they need it.",
    },
    {
        n: "05",
        title: "Reporting & optimization",
        body: "FreeByte reports results back to the brand and continuously optimizes the campaign.",
    },
];

const HowItWorks = () => {
    return (
        <main data-testid="how-it-works-page">
            {/* Hero */}
            <section className="pt-28 md:pt-32 pb-12 md:pb-16">
                <div className="max-w-6xl mx-auto px-6 md:px-10">
                    <Reveal>
                        <p
                            data-testid="hiw-eyebrow"
                            className="text-[11px] uppercase tracking-[0.22em] text-neutral-500"
                        >
                            How it works
                        </p>
                    </Reveal>
                    <Reveal delay={120}>
                        <h1
                            data-testid="hiw-headline"
                            className="headline-display mt-4 text-2xl sm:text-3xl lg:text-4xl font-semibold text-black max-w-3xl"
                        >
                            How FreeByte{" "}
                            <span className="text-neutral-400">Works</span>
                        </h1>
                    </Reveal>
                    <Reveal delay={220}>
                        <p
                            data-testid="hiw-intro"
                            className="mt-6 text-sm md:text-base text-neutral-600 max-w-xl leading-relaxed"
                        >
                            FreeByte is designed to make mobile rewards simple,
                            flexible and easy to use.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* Horizontal flow */}
            <section
                data-testid="hiw-flow-section"
                className="border-t border-black/5 py-16 md:py-20"
            >
                <div className="max-w-6xl mx-auto px-6 md:px-10">
                    {/* Connecting line — desktop */}
                    <div className="relative hidden lg:block mb-10">
                        <div className="absolute left-0 right-0 top-1/2 h-px bg-black/10" />
                        <div className="relative grid grid-cols-5">
                            {steps.map((s, idx) => (
                                <div
                                    key={s.n}
                                    className="flex justify-center"
                                >
                                    <Reveal delay={140 * idx}>
                                        <div
                                            className="w-8 h-8 rounded-full bg-white border border-black/15 flex items-center justify-center"
                                            data-testid={`hiw-flow-node-${s.n}`}
                                        >
                                            <span className="w-1.5 h-1.5 rounded-full bg-black" />
                                        </div>
                                    </Reveal>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5 md:gap-6">
                        {steps.map((s, idx) => (
                            <Reveal key={s.n} delay={120 * idx}>
                                <div
                                    data-testid={`hiw-step-card-${s.n}`}
                                    className="group h-full rounded-2xl border border-black/10 bg-white p-6 md:p-7 transition-all duration-500 hover:border-black/40 hover:shadow-[0_20px_60px_-30px_rgba(0,0,0,0.2)]"
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="text-[11px] tracking-[0.22em] uppercase text-neutral-400">
                                            Step {s.n}
                                        </span>
                                        <span className="w-5 h-px bg-black/10 group-hover:bg-black transition-colors duration-500" />
                                    </div>
                                    <h3 className="mt-6 text-base md:text-lg font-semibold text-black tracking-tight leading-snug">
                                        {s.title}
                                    </h3>
                                    <p className="mt-2.5 text-[13px] text-neutral-600 leading-relaxed">
                                        {s.body}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Closing note + CTA */}
            <section
                data-testid="hiw-cta-section"
                className="border-t border-black/5 py-20 md:py-24"
            >
                <div className="max-w-3xl mx-auto px-6 md:px-10 text-center">
                    <Reveal>
                        <p className="text-[11px] uppercase tracking-[0.22em] text-neutral-500">
                            Simple. Flexible. Rewarding.
                        </p>
                    </Reveal>
                    <Reveal delay={120}>
                        <h2
                            data-testid="hiw-cta-headline"
                            className="headline-display mt-4 text-2xl sm:text-3xl lg:text-4xl font-semibold text-black"
                        >
                            Bring rewarded growth to your{" "}
                            <span className="text-neutral-400">brand.</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={240}>
                        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                            <a
                                href={MAILTO}
                                data-testid="hiw-contact-btn"
                                className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-black text-white text-[13px] font-medium tracking-wide hover:bg-neutral-800 transition-colors"
                            >
                                Contact Us
                                <ArrowUpRight size={15} />
                            </a>
                            <Link
                                to="/"
                                data-testid="hiw-back-home-btn"
                                className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-black/15 text-black text-[13px] font-medium tracking-wide hover:border-black transition-colors group"
                            >
                                Back to Home
                                <ArrowRight
                                    size={15}
                                    className="transition-transform group-hover:translate-x-0.5"
                                />
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </section>
        </main>
    );
};

export default HowItWorks;
